import React, { useState, useCallback, useMemo, useEffect } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import {
  DASTicketDetails,
  TakeActionType,
  ApprovalPosition,
  RelatedTicketDisplay,
  ApprovalLevelWithDynamic,
  TakeActionRequest,
  EnhancedAttachment,
} from '../../../api/types/dasTickets.types';
import { User } from '../../../api/types/auth.types';
import { ID } from '../../../api/types/common.types';
import { EligibleReturnRecipient } from '../../../api/types/das/approvalActions.types';
import { approvalActionsService } from '../../../api/services/das/approvalActionsService';
import { useFileUpload, useFileUploadSettings } from '../../../hooks/api/das/useAttachments';
import { useActiveRequestTypes } from '../../../hooks/api/useRequestTypes';
import type { RequestTypeEntity } from '../../../api/types';
import ApprovalFlowDiagram from './ApprovalFlowDiagram';
import ActionHistoryTimeline from './ActionHistoryTimeline';

/**
 * TakeActionModal Props.
 */
export interface TakeActionModalProps {
  ticket: DASTicketDetails;
  currentUserPosition: ApprovalPosition;
  relatedTickets?: RelatedTicketDisplay[];
  approvalLevels?: ApprovalLevelWithDynamic[];
  availableUsers?: User[]; // Users available for forwarding or adding as approvers
  onClose: () => void;
  onActionTaken: (request: TakeActionRequest) => Promise<void>;
}

/**
 * TakeActionModal Component
 *
 * Modal for approvers to take action on tickets (Approve, Reject, Request Changes, etc.)
 *
 * Phase 1: Basic functionality with Approve, Reject, Request Changes
 */
const TakeActionModal: React.FC<TakeActionModalProps> = ({
  ticket,
  currentUserPosition,
  relatedTickets = [],
  approvalLevels = [],
  availableUsers = [],
  onClose,
  onActionTaken,
}) => {
  // State - Basic
  const [selectedAction, setSelectedAction] = useState<TakeActionType | null>(null);
  const [comments, setComments] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  // State - Phase 2: Forward Action
  const [forwardToUserId, setForwardToUserId] = useState<ID | null>(null);
  const [forwardReason, setForwardReason] = useState('');

  // State - Return for Clarification: Recipient Selection
  const [returnToUserId, setReturnToUserId] = useState<ID | null>(null);
  const [eligibleReturnRecipients, setEligibleReturnRecipients] = useState<EligibleReturnRecipient[]>([]);
  const [loadingRecipients, setLoadingRecipients] = useState(false);

  // State - Phase 2: Add Approvers
  const [selectedApproverIds, setSelectedApproverIds] = useState<ID[]>([]);
  const [approvalMode, setApprovalMode] = useState<'sequential' | 'parallel'>('sequential');
  const [additionReason, setAdditionReason] = useState('');

  // State - File Upload
  const [selectedFiles, setSelectedFiles] = useState<EnhancedAttachment[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{ [key: string]: number }>({});
  const [filePreviews, setFilePreviews] = useState<{ [key: string]: string }>({});

  // File upload settings. Two sources are combined:
  //   1. Global defaults from useFileUploadSettings (system-wide fallback).
  //   2. Per-request-type overrides from admin.tbl_request_type (maxFileSizeKB
  //      / maxTotalSizeKB / allowedExtensions). When the admin configured
  //      higher limits on a specific request type, the resubmit flow must
  //      honour those — not the smaller global default.
  // This mirrors NewTicketModal so create and resubmit use identical limits.
  const { limits: fileUploadLimits } = useFileUploadSettings();
  const { requestTypes } = useActiveRequestTypes();

  const selectedRequestType = useMemo<RequestTypeEntity | null>(
    () => requestTypes.find((rt) => rt.id === ticket.requestTypeId) ?? null,
    [requestTypes, ticket.requestTypeId]
  );

  const effectiveFileLimits = useMemo(() => {
    if (!selectedRequestType) return fileUploadLimits;
    return {
      maxIndividualSize: selectedRequestType.maxFileSizeKB
        ? selectedRequestType.maxFileSizeKB * 1024
        : fileUploadLimits.maxIndividualSize,
      maxTotalSize: selectedRequestType.maxTotalSizeKB
        ? selectedRequestType.maxTotalSizeKB * 1024
        : fileUploadLimits.maxTotalSize,
      allowedExtensions: selectedRequestType.allowedExtensions
        ? selectedRequestType.allowedExtensions
        : fileUploadLimits.allowedExtensions,
      allowedExtensionsDisplay: selectedRequestType.allowedExtensions
        ? selectedRequestType.allowedExtensions.join(', ')
        : fileUploadLimits.allowedExtensionsDisplay,
    };
  }, [selectedRequestType, fileUploadLimits]);

  const { validateFile, getFormattedTotalSize, fileSizeLimits } = useFileUpload(effectiveFileLimits);

  // State - Confirmation Dialog
  const [showConfirmation, setShowConfirmation] = useState(false);

  // Reset all form state when modal opens (ticket changes)
  useEffect(() => {
    // Reset action selection
    setSelectedAction(null);
    setComments('');
    setValidationError(null);

    // Reset forward action state
    setForwardToUserId(null);
    setForwardReason('');

    // Reset return for clarification state
    setReturnToUserId(null);
    setEligibleReturnRecipients([]);

    // Reset add approvers state
    setSelectedApproverIds([]);
    setApprovalMode('sequential');
    setAdditionReason('');

    // Reset file upload state
    setSelectedFiles([]);
    setFilePreviews({});
    setUploadProgress({});

    // Reset confirmation dialog
    setShowConfirmation(false);
  }, [ticket.id]);

  // Fetch eligible return recipients when "request-changes" action is selected
  useEffect(() => {
    const fetchEligibleRecipients = async () => {
      if (selectedAction === 'request-changes') {
        setLoadingRecipients(true);
        try {
          const recipients = await approvalActionsService.getEligibleReturnRecipients(String(ticket.id));
          setEligibleReturnRecipients(recipients);
          // Auto-select the recommended recipient (original requestor) if available
          const recommended = recipients.find(r => r.isRecommended);
          if (recommended) {
            setReturnToUserId(recommended.userId);
          } else if (recipients.length > 0) {
            setReturnToUserId(recipients[0].userId);
          }
        } catch (error) {
          console.error('Failed to fetch eligible return recipients:', error);
          setEligibleReturnRecipients([]);
        } finally {
          setLoadingRecipients(false);
        }
      } else {
        // Reset when switching away from request-changes
        setReturnToUserId(null);
        setEligibleReturnRecipients([]);
      }
    };

    fetchEligibleRecipients();
  }, [selectedAction, ticket.id]);

  // State - Document Preview
  const [previewDocument, setPreviewDocument] = useState<{
    url: string;
    fileName: string;
    fileType: string;
  } | null>(null);

  // Available actions based on user position
  const availableActions = useMemo(() => {
    const actions: Array<{
      type: TakeActionType;
      label: string;
      color: string;
      description: string;
      requiresComments: boolean;
      minCommentsLength?: number;
      requiresUserSelection?: boolean;
      requiresMultipleUserSelection?: boolean;
    }> = [];

    // Phase 2: Withdraw action (if user has already approved)
    if (currentUserPosition.hasActioned && currentUserPosition.actionTaken === 'approve') {
      actions.push({
        type: 'withdraw',
        label: 'Withdraw Approval',
        color: '#8b5cf6', // purple
        description: 'Retract your previous approval',
        requiresComments: true,
        minCommentsLength: 10,
      });
      // If user has already actioned, they can only withdraw - no other actions available
      return actions;
    }

    // Only show approval actions if user can take action
    if (!currentUserPosition.canTakeAction) {
      return actions;
    }

    // Basic actions available to all approvers
    actions.push({
      type: 'approve',
      label: 'Approve',
      color: '#10b981', // green
      description: 'Approve this request and move to next level',
      requiresComments: false,
    });

    actions.push({
      type: 'reject',
      label: 'Reject',
      color: 'var(--danger)', // red
      description: 'Reject this request with reason',
      requiresComments: true,
      minCommentsLength: 10,
    });

    actions.push({
      type: 'request-changes',
      label: 'Request Changes',
      color: 'var(--warning)', // orange
      description: 'Return to requester for modifications',
      requiresComments: true,
      minCommentsLength: 10,
    });

    // Phase 2: Forward action - Hidden for now, will be enabled later
    // actions.push({
    //   type: 'forward',
    //   label: 'Forward',
    //   color: '#3b82f6', // blue
    //   description: 'Delegate this approval to another user',
    //   requiresComments: true,
    //   minCommentsLength: 10,
    //   requiresUserSelection: true,
    // });

    // Phase 2: Add approvers action - Hidden for now, will be enabled later
    // actions.push({
    //   type: 'add-approvers',
    //   label: 'Add Approvers',
    //   color: '#06b6d4', // cyan
    //   description: 'Insert additional approvers into workflow',
    //   requiresComments: true,
    //   minCommentsLength: 10,
    //   requiresMultipleUserSelection: true,
    // });

    return actions;
  }, [currentUserPosition.canTakeAction, currentUserPosition.hasActioned, currentUserPosition.actionTaken]);

  // Get selected action details
  const selectedActionDetails = useMemo(() => {
    return availableActions.find((a) => a.type === selectedAction);
  }, [availableActions, selectedAction]);

  // Validation
  const isFormValid = useMemo(() => {
    if (!selectedAction) return false;

    const actionDetails = selectedActionDetails;
    if (!actionDetails) return false;

    // Check if comments are required
    if (actionDetails.requiresComments) {
      if (!comments.trim()) return false;
      if (actionDetails.minCommentsLength && comments.trim().length < actionDetails.minCommentsLength) {
        return false;
      }
    }

    // Phase 2: Forward action validation
    if (selectedAction === 'forward') {
      if (!forwardToUserId) return false;
      if (!forwardReason.trim() || forwardReason.trim().length < 10) return false;
    }

    // Phase 2: Add approvers validation
    if (selectedAction === 'add-approvers') {
      if (selectedApproverIds.length === 0) return false;
      if (!additionReason.trim() || additionReason.trim().length < 10) return false;
    }

    return true;
  }, [
    selectedAction,
    comments,
    selectedActionDetails,
    forwardToUserId,
    forwardReason,
    selectedApproverIds,
    additionReason,
  ]);

  // Handle action selection
  const handleSelectAction = useCallback((action: TakeActionType) => {
    setSelectedAction(action);
    setValidationError(null);
  }, []);

  // Handle submit - Show confirmation dialog
  const handleSubmit = useCallback(() => {
    if (!isFormValid || !selectedAction) {
      setValidationError('Please complete all required fields');
      return;
    }

    const actionDetails = selectedActionDetails;
    if (!actionDetails) return;

    // Validate comments if required
    if (actionDetails.requiresComments && comments.trim().length < (actionDetails.minCommentsLength || 0)) {
      setValidationError(
        `Please provide detailed comments (minimum ${actionDetails.minCommentsLength} characters)`
      );
      return;
    }

    // Phase 2: Additional validation for forward action
    if (selectedAction === 'forward') {
      if (!forwardToUserId) {
        setValidationError('Please select a user to forward to');
        return;
      }
      if (!forwardReason.trim() || forwardReason.trim().length < 10) {
        setValidationError('Please provide a detailed reason for forwarding (minimum 10 characters)');
        return;
      }
    }

    // Phase 2: Additional validation for add approvers
    if (selectedAction === 'add-approvers') {
      if (selectedApproverIds.length === 0) {
        setValidationError('Please select at least one approver to add');
        return;
      }
      if (!additionReason.trim() || additionReason.trim().length < 10) {
        setValidationError('Please provide a detailed reason for adding approvers (minimum 10 characters)');
        return;
      }
    }

    // All validation passed - show confirmation dialog
    setValidationError(null);
    setShowConfirmation(true);
  }, [
    isFormValid,
    selectedAction,
    selectedActionDetails,
    comments,
    forwardToUserId,
    forwardReason,
    selectedApproverIds,
    additionReason,
  ]);

  // Confirm and submit action
  const confirmAndSubmit = useCallback(async () => {
    if (!selectedAction) return;

    setIsSubmitting(true);
    setShowConfirmation(false);
    setValidationError(null);

    try {
      // Build TakeActionRequest object
      const request: TakeActionRequest = {
        ticketId: ticket.id,
        action: selectedAction,
        comments: comments.trim(),
      };

      // Add rowVersion for optimistic locking
      if (ticket.rowVersion !== undefined && ticket.rowVersion !== null) {
        request.rowVersion = typeof ticket.rowVersion === 'number'
          ? ticket.rowVersion
          : parseInt(String(ticket.rowVersion), 10) || 0;
      }

      // Add forward-specific fields
      if (selectedAction === 'forward' && forwardToUserId) {
        request.forwardToUserId = forwardToUserId;
        request.forwardReason = forwardReason.trim();
      }

      // Add return-for-clarification-specific fields
      if (selectedAction === 'request-changes') {
        if (returnToUserId) {
          request.returnToUserId = returnToUserId;
        }
      }

      // Add approvers-specific fields
      if (selectedAction === 'add-approvers') {
        request.additionalApproverIds = selectedApproverIds;
        request.approvalMode = approvalMode;
        request.additionReason = additionReason.trim();
      }

      // Add file attachments if any
      if (selectedFiles.length > 0) {
        request.attachments = selectedFiles;
      }

      await onActionTaken(request);
      // Modal will be closed by parent component after successful action
    } catch (error: any) {
      console.error('Failed to submit action:', error);

      // Check for conflict error (optimistic locking)
      if (error?.response?.status === 409 || error?.response?.data?.code === 'CONFLICT') {
        const conflictData = error.response?.data;
        setValidationError(
          `This ticket has been modified by ${conflictData?.conflictingUser || 'another user'}. Please refresh and try again.`
        );
      } else {
        // Extract error message from API response or error object
        const errorMessage =
          error?.response?.data?.message ||
          error?.response?.data?.errors?.[0] ||
          error?.message ||
          'Failed to submit action. Please try again.';
        setValidationError(errorMessage);
      }

      setIsSubmitting(false);
    }
  }, [
    selectedAction,
    comments,
    forwardToUserId,
    forwardReason,
    returnToUserId,
    selectedApproverIds,
    approvalMode,
    additionReason,
    selectedFiles,
    ticket.id,
    ticket.rowVersion,
    onActionTaken,
  ]);

  // Handle close
  const handleClose = useCallback(() => {
    if (isSubmitting) return;
    if (selectedAction || comments) {
      if (!window.confirm('You have unsaved changes. Are you sure you want to close?')) {
        return;
      }
    }
    onClose();
  }, [isSubmitting, selectedAction, comments, onClose]);

  // Format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Get priority color - aligned with backend priority values
  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'Urgent':
        return '#dc2626';
      case 'High':
        return '#f59e0b';
      case 'Medium':
        return '#3b82f6';
      case 'Normal':
      default:
        return '#64748b';
    }
  };

  // Get status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Approved':
        return '#10b981';
      case 'Pending':
      case 'In Progress':
        return '#f59e0b';
      case 'Rejected':
        return '#ef4444';
      case 'Closed':
        return '#64748b';
      default:
        return '#64748b';
    }
  };

  // Get file icon color based on file type
  const getFileIconColor = (fileType: string) => {
    if (fileType.startsWith('image/')) {
      return '#3b82f6'; // blue for images
    } else if (fileType.includes('pdf')) {
      return '#ef4444'; // red for PDFs
    } else if (fileType.includes('word') || fileType.includes('document')) {
      return '#3b82f6'; // blue for documents
    } else if (fileType.includes('excel') || fileType.includes('sheet')) {
      return '#10b981'; // green for spreadsheets
    } else {
      return '#64748b'; // gray for others
    }
  };

  // Format file size
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  // File upload handlers
  const handleFileSelect = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      if (!files || files.length === 0) return;

      // Get existing files for validation
      const existingFiles = selectedFiles.map((f) => f.file);

      // Validate and filter files
      const validFiles: EnhancedAttachment[] = [];
      const errors: string[] = [];

      Array.from(files).forEach((file) => {
        // Validate file size against limits
        const validation = validateFile(file, [...existingFiles, ...validFiles.map((f) => f.file)]);
        if (!validation.isValid) {
          errors.push(`${file.name}: ${validation.errorMessage}`);
        } else {
          validFiles.push({
            file,
            title: file.name.replace(/\.[^/.]+$/, ''), // Remove extension for default title
            description: '',
          });
        }
      });

      // Show validation errors if any
      if (errors.length > 0) {
        alert(`File validation failed:\n${errors.join('\n')}`);
      }

      // Only add valid files
      if (validFiles.length > 0) {
        setSelectedFiles((prev) => [...prev, ...validFiles]);

        // Generate previews for image files
        validFiles.forEach(({ file }) => {
          if (file.type.startsWith('image/')) {
            const reader = new FileReader();
            reader.onloadend = () => {
              setFilePreviews((prev) => ({
                ...prev,
                [file.name]: reader.result as string,
              }));
            };
            reader.readAsDataURL(file);
          }
        });

        // Simulate upload progress
        validFiles.forEach(({ file }) => {
          setUploadProgress((prev) => ({ ...prev, [file.name]: 0 }));
          let progress = 0;
          const interval = setInterval(() => {
            progress += 10;
            if (progress >= 100) {
              clearInterval(interval);
              setUploadProgress((prev) => ({ ...prev, [file.name]: 100 }));
            } else {
              setUploadProgress((prev) => ({ ...prev, [file.name]: progress }));
            }
          }, 100);
        });
      }

      // Reset input value to allow selecting the same file again
      event.target.value = '';
    },
    [selectedFiles, validateFile]
  );

  const handleDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
  }, []);

  const handleDragEnter = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);

    const files = event.dataTransfer.files;
    if (!files || files.length === 0) return;

    // Get existing files for validation
    const existingFiles = selectedFiles.map((f) => f.file);

    // Validate and filter files
    const validFiles: EnhancedAttachment[] = [];
    const errors: string[] = [];

    Array.from(files).forEach((file) => {
      // Validate file size against limits
      const validation = validateFile(file, [...existingFiles, ...validFiles.map((f) => f.file)]);
      if (!validation.isValid) {
        errors.push(`${file.name}: ${validation.errorMessage}`);
      } else {
        validFiles.push({
          file,
          title: file.name.replace(/\.[^/.]+$/, ''), // Remove extension for default title
          description: '',
        });
      }
    });

    // Show validation errors if any
    if (errors.length > 0) {
      alert(`File validation failed:\n${errors.join('\n')}`);
    }

    // Only add valid files
    if (validFiles.length > 0) {
      setSelectedFiles((prev) => [...prev, ...validFiles]);

      // Generate previews for image files
      validFiles.forEach(({ file }) => {
        if (file.type.startsWith('image/')) {
          const reader = new FileReader();
          reader.onloadend = () => {
            setFilePreviews((prev) => ({
              ...prev,
              [file.name]: reader.result as string,
            }));
          };
          reader.readAsDataURL(file);
        }
      });

      // Simulate upload progress
      validFiles.forEach(({ file }) => {
        setUploadProgress((prev) => ({ ...prev, [file.name]: 0 }));
        let progress = 0;
        const interval = setInterval(() => {
          progress += 10;
          if (progress >= 100) {
            clearInterval(interval);
            setUploadProgress((prev) => ({ ...prev, [file.name]: 100 }));
          } else {
            setUploadProgress((prev) => ({ ...prev, [file.name]: progress }));
          }
        }, 100);
      });
    }
  }, [selectedFiles, validateFile]);

  const handleRemoveFile = useCallback((index: number) => {
    const fileName = selectedFiles[index]?.file.name;
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
    if (fileName) {
      setFilePreviews((prev) => {
        const newPreviews = { ...prev };
        delete newPreviews[fileName];
        return newPreviews;
      });
      setUploadProgress((prev) => {
        const newProgress = { ...prev };
        delete newProgress[fileName];
        return newProgress;
      });
    }
  }, [selectedFiles]);

  // Update attachment metadata
  const handleUpdateAttachment = useCallback((index: number, field: 'title' | 'description', value: string) => {
    setSelectedFiles((prev) =>
      prev.map((attachment, i) => (i === index ? { ...attachment, [field]: value } : attachment))
    );
  }, []);

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'var(--color-shadow, rgba(0,0,0,0.5))',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10050,
      }}
    >
      <div
        style={{
          background: 'var(--bg-primary)',
          borderRadius: '0.5rem',
          width: '90%',
          maxWidth: '1000px',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 25px 50px -12px var(--color-shadow, rgba(0,0,0,0.25))',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '1.5rem',
            borderBottom: '2px solid var(--border-color)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            background: 'var(--bg-secondary)',
            borderTopLeftRadius: '0.5rem',
            borderTopRightRadius: '0.5rem',
          }}
        >
          <div>
            <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 600, color: 'var(--text-primary)' }}>
              Take Action
            </h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.25rem' }}>
              <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                Request: <strong>{ticket.ticketNo}</strong>
              </span>
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: '0.125rem 0.5rem',
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  borderRadius: '0.25rem',
                  background: `${getPriorityColor(ticket.priority)}15`,
                  color: getPriorityColor(ticket.priority),
                  textTransform: 'uppercase',
                }}
              >
                {ticket.priority}
              </span>
            </div>
          </div>
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
              padding: '0.5rem',
              opacity: isSubmitting ? 0.5 : 1,
            }}
          >
            <IconSystem.x size={24} />
          </button>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '1.5rem' }}>
          {/* Cannot Take Action Message */}
          {!currentUserPosition.canTakeAction && (
            <div
              style={{
                padding: '1rem',
                background: '#fef3c7',
                border: '1px solid #fbbf24',
                borderRadius: '0.5rem',
                marginBottom: '1.5rem',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span style={{ fontSize: '1.25rem' }}>ℹ️</span>
                <div>
                  <strong style={{ color: '#92400e' }}>No Action Required</strong>
                  <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.875rem', color: '#92400e' }}>
                    {currentUserPosition.hasActioned
                      ? `You have already ${currentUserPosition.actionTaken} this ticket on ${
                          currentUserPosition.actionDate
                            ? formatDate(currentUserPosition.actionDate)
                            : 'N/A'
                        }`
                      : 'This ticket is not currently at your approval level'}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Request Details */}
          <div style={{ marginBottom: '1.5rem' }}>
            <h3
              style={{
                fontSize: '1.125rem',
                fontWeight: 600,
                marginBottom: '1rem',
                color: 'var(--text-primary)',
                borderBottom: '2px solid var(--border-color)',
                paddingBottom: '0.5rem',
              }}
            >
              Request Details
            </h3>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: '1rem',
                padding: '1rem',
                background: 'var(--bg-secondary)',
                borderRadius: '0.5rem',
                border: '1px solid var(--border-color)',
              }}
            >
              {/* Subject */}
              <div style={{ gridColumn: 'span 2' }}>
                <label style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', fontWeight: 500 }}>Subject</label>
                <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.875rem', fontWeight: 600 }}>
                  {ticket.subject}
                </p>
              </div>

              {/* Request Type */}
              <div>
                <label style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', fontWeight: 500 }}>Request Type</label>
                <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.875rem' }}>{ticket.requestType}</p>
              </div>

              {/* Priority */}
              <div>
                <label style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', fontWeight: 500 }}>Priority</label>
                <p
                  style={{
                    margin: '0.25rem 0 0 0',
                    fontSize: '0.875rem',
                    color: getPriorityColor(ticket.priority),
                    fontWeight: 600,
                  }}
                >
                  {ticket.priority}
                </p>
              </div>

              {/* Created By */}
              <div>
                <label style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', fontWeight: 500 }}>Created By</label>
                <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.875rem' }}>{ticket.createdByUserName}</p>
              </div>

              {/* Pending Since */}
              <div>
                <label style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', fontWeight: 500 }}>Pending Since</label>
                <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.875rem' }}>
                  {formatDate(ticket.pendingSince)} ({ticket.pendingAge} days)
                </p>
              </div>

              {/* Value */}
              {ticket.value !== undefined && ticket.value !== null && (
                <div style={{ gridColumn: 'span 2' }}>
                  <label style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', fontWeight: 500 }}>Value</label>
                  <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.875rem', fontWeight: 600, color: 'var(--primary)' }}>
                    {ticket.value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                </div>
              )}

              {/* Description */}
              <div style={{ gridColumn: 'span 2' }}>
                <label style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', fontWeight: 500 }}>Description</label>
                <p
                  style={{
                    margin: '0.25rem 0 0 0',
                    fontSize: '0.875rem',
                    lineHeight: '1.5',
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {ticket.description}
                </p>
              </div>
            </div>
          </div>

          {/* Attachments */}
          {ticket.attachments && ticket.attachments.length > 0 && (
            <div style={{ marginBottom: '1.5rem' }}>
              <h3
                style={{
                  fontSize: '1.125rem',
                  fontWeight: 600,
                  marginBottom: '1rem',
                  color: 'var(--text-primary)',
                  borderBottom: '2px solid #e5e7eb',
                  paddingBottom: '0.5rem',
                }}
              >
                Attachments ({ticket.attachments.length})
              </h3>

              <div
                style={{
                  background: 'var(--bg-secondary)',
                  border: '1px solid #e2e8f0',
                  borderRadius: '0.5rem',
                  padding: '1rem',
                }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {ticket.attachments.map((attachment) => (
                    <div
                      key={attachment.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.75rem',
                        padding: '0.75rem',
                        background: 'var(--bg-primary)',
                        border: '1px solid #e2e8f0',
                        borderRadius: '0.375rem',
                        transition: 'all 0.2s',
                      }}
                    >
                      {/* File Icon */}
                      <div
                        style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '0.375rem',
                          background: `${getFileIconColor(attachment.fileType)}15`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        <IconSystem.file size={20} color={getFileIconColor(attachment.fileType)} />
                      </div>

                      {/* File Details */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            fontSize: '0.875rem',
                            fontWeight: 600,
                            color: 'var(--text-primary)',
                            marginBottom: '0.25rem',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {attachment.fileName}
                        </div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                          {formatFileSize(attachment.fileSize)} • Uploaded by {attachment.uploadedBy} on{' '}
                          {formatDate(attachment.uploadedAt)}
                        </div>
                      </div>

                      {/* Preview & Download Buttons */}
                      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                        {/* Preview Button (for images and PDFs) */}
                        {(attachment.fileType.startsWith('image/') || attachment.fileType.includes('pdf')) && (
                          <button
                            type="button"
                            onClick={async (e) => {
                              e.stopPropagation();
                              try {
                                // Fetch blob URL for preview (same as TicketAttachments component)
                                const { attachmentsService } = await import('../../../api/services/das/attachmentsService');
                                const blobUrl = await attachmentsService.getPreviewUrl(attachment.id, attachment.fileType);
                                setPreviewDocument({
                                  url: blobUrl,
                                  fileName: attachment.fileName,
                                  fileType: attachment.fileType,
                                });
                              } catch (error) {
                                console.error('Failed to load preview:', error);
                                alert('Failed to load document preview. Please try downloading the file instead.');
                              }
                            }}
                            style={{
                              padding: '0.5rem 0.75rem',
                              background: '#10b981',
                              color: 'white',
                              border: 'none',
                              borderRadius: '0.375rem',
                              fontSize: '0.75rem',
                              fontWeight: 500,
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.25rem',
                              cursor: 'pointer',
                              flexShrink: 0,
                              transition: 'background 0.2s',
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = '#059669';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = '#10b981';
                            }}
                          >
                            <IconSystem.eye size={14} />
                            Preview
                          </button>
                        )}

                        {/* Download Button */}
                        <a
                          href={attachment.downloadUrl}
                          download={attachment.fileName}
                          style={{
                            padding: '0.5rem 0.75rem',
                            background: 'var(--primary)',
                            color: 'var(--color-text-inverse, #ffffff)',
                            borderRadius: '0.375rem',
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            textDecoration: 'none',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.25rem',
                            flexShrink: 0,
                            transition: 'background 0.2s',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = '#2563eb';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = '#3b82f6';
                          }}
                        >
                          <IconSystem.download size={14} />
                          Download
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Related Requests (if any) */}
          {relatedTickets.length > 0 && (
            <div style={{ marginBottom: '1.5rem' }}>
              <h3
                style={{
                  fontSize: '1.125rem',
                  fontWeight: 600,
                  marginBottom: '1rem',
                  color: 'var(--text-primary)',
                  borderBottom: '2px solid #e5e7eb',
                  paddingBottom: '0.5rem',
                }}
              >
                Related Requests ({relatedTickets.length})
              </h3>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {relatedTickets.map((relatedTicket) => (
                  <div
                    key={relatedTicket.ticketId}
                    style={{
                      padding: '0.75rem',
                      background: 'var(--bg-secondary)',
                      border: '1px solid #e2e8f0',
                      borderRadius: '0.375rem',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.25rem' }}>
                        {relatedTicket.ticketNo}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{relatedTicket.subject}</div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>
                        <span style={{ color: getStatusColor(relatedTicket.status), fontWeight: 500 }}>
                          {relatedTicket.status}
                        </span>
                        {' • '}
                        {relatedTicket.relationshipType}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Phase 2: Approval Flow Diagram */}
          {approvalLevels.length > 0 && (
            <ApprovalFlowDiagram approvalLevels={approvalLevels} currentUserPosition={currentUserPosition} />
          )}

          {/* Phase 2: Action History Timeline */}
          {ticket.workflowHistory && ticket.workflowHistory.length > 0 && (
            <ActionHistoryTimeline history={ticket.workflowHistory} />
          )}

          {/* Action Selection (only if user can take action) */}
          {currentUserPosition.canTakeAction && (
            <>
              <div style={{ marginBottom: '1.5rem' }}>
                <h3
                  style={{
                    fontSize: '1.125rem',
                    fontWeight: 600,
                    marginBottom: '1rem',
                    color: 'var(--text-primary)',
                    borderBottom: '2px solid #e5e7eb',
                    paddingBottom: '0.5rem',
                  }}
                >
                  Select Action *
                </h3>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem' }}>
                  {availableActions.map((action) => (
                    <button
                      key={action.type}
                      type="button"
                      onClick={() => handleSelectAction(action.type)}
                      disabled={isSubmitting}
                      style={{
                        padding: '1rem',
                        border: `2px solid ${
                          selectedAction === action.type ? action.color : '#e2e8f0'
                        }`,
                        background:
                          selectedAction === action.type
                            ? `${action.color}15`
                            : 'white',
                        borderRadius: '0.5rem',
                        cursor: isSubmitting ? 'not-allowed' : 'pointer',
                        transition: 'all 0.2s',
                        textAlign: 'center',
                        opacity: isSubmitting ? 0.5 : 1,
                      }}
                    >
                      <div
                        style={{
                          fontSize: '1rem',
                          fontWeight: 600,
                          color: selectedAction === action.type ? action.color : '#1e293b',
                          marginBottom: '0.25rem',
                        }}
                      >
                        {action.label}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                        {action.description}
                      </div>
                      {action.requiresComments && (
                        <div
                          style={{
                            fontSize: '0.625rem',
                            color: 'var(--warning)',
                            marginTop: '0.25rem',
                            fontWeight: 500,
                          }}
                        >
                          Comments required
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Comments Section */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label
                  style={{
                    display: 'block',
                    fontSize: '1rem',
                    fontWeight: 600,
                    marginBottom: '0.5rem',
                    color: 'var(--text-primary)',
                  }}
                >
                  Comments
                  {selectedActionDetails?.requiresComments && (
                    <span style={{ color: 'var(--danger)' }}> *</span>
                  )}
                </label>
                <textarea
                  value={comments}
                  onChange={(e) => {
                    setComments(e.target.value);
                    setValidationError(null);
                  }}
                  placeholder={
                    selectedActionDetails?.requiresComments
                      ? `Please provide detailed comments (minimum ${selectedActionDetails.minCommentsLength} characters)`
                      : 'Optional comments about your decision'
                  }
                  rows={4}
                  disabled={isSubmitting}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #cbd5e1',
                    borderRadius: '0.375rem',
                    fontSize: '0.875rem',
                    fontFamily: 'inherit',
                    resize: 'vertical',
                  }}
                />
                {selectedActionDetails?.requiresComments && (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      fontSize: '0.75rem',
                      marginTop: '0.25rem',
                      color:
                        comments.trim().length < (selectedActionDetails.minCommentsLength || 0)
                          ? '#dc2626'
                          : '#64748b',
                    }}
                  >
                    <span>
                      {comments.trim().length < (selectedActionDetails.minCommentsLength || 0)
                        ? `Minimum ${selectedActionDetails.minCommentsLength} characters required`
                        : 'Valid'}
                    </span>
                    <span>
                      {comments.trim().length} / {selectedActionDetails.minCommentsLength}
                    </span>
                  </div>
                )}
              </div>

              {/* Return for Clarification - Recipient Selection */}
              {selectedAction === 'request-changes' && (
                <div style={{ marginBottom: '1.5rem' }}>
                  <label
                    style={{
                      display: 'block',
                      fontSize: '1rem',
                      fontWeight: 600,
                      marginBottom: '0.5rem',
                      color: 'var(--text-primary)',
                    }}
                  >
                    Return To <span style={{ color: 'var(--danger)' }}>*</span>
                  </label>
                  {loadingRecipients ? (
                    <div
                      style={{
                        padding: '0.75rem',
                        background: 'var(--bg-secondary)',
                        border: '1px solid #cbd5e1',
                        borderRadius: '0.375rem',
                        fontSize: '0.875rem',
                        color: 'var(--text-secondary)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                      }}
                    >
                      <span style={{ animation: 'spin 1s linear infinite' }}>⏳</span>
                      Loading eligible recipients...
                    </div>
                  ) : eligibleReturnRecipients.length === 0 ? (
                    <div
                      style={{
                        padding: '0.75rem',
                        background: '#fef3c7',
                        border: '1px solid #fde68a',
                        borderRadius: '0.375rem',
                        fontSize: '0.875rem',
                        color: '#92400e',
                      }}
                    >
                      No eligible recipients found. The request will be returned to the original requestor.
                    </div>
                  ) : (
                    <>
                      <select
                        value={returnToUserId || ''}
                        onChange={(e) => {
                          setReturnToUserId(e.target.value);
                          setValidationError(null);
                        }}
                        disabled={isSubmitting}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '1px solid #cbd5e1',
                          borderRadius: '0.375rem',
                          fontSize: '0.875rem',
                          fontFamily: 'inherit',
                          background: 'var(--bg-primary)',
                        }}
                      >
                        <option value="">Select a recipient...</option>
                        {eligibleReturnRecipients.map((recipient) => (
                          <option key={recipient.userId} value={recipient.userId}>
                            {recipient.userName}
                            {recipient.recipientType === 'Requestor'
                              ? ' (Original Requestor)'
                              : ` (${recipient.levelName || `Level ${recipient.levelNumber}`} - ${recipient.actionTaken})`}
                            {recipient.isRecommended ? ' ★ Recommended' : ''}
                          </option>
                        ))}
                      </select>
                      <div
                        style={{
                          fontSize: '0.75rem',
                          color: 'var(--text-secondary)',
                          marginTop: '0.5rem',
                        }}
                      >
                        Select who should receive and respond to this return request. The original requestor is recommended.
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Phase 2: Forward Action - User Selection */}
              {selectedAction === 'forward' && (
                <div style={{ marginBottom: '1.5rem' }}>
                  <label
                    style={{
                      display: 'block',
                      fontSize: '1rem',
                      fontWeight: 600,
                      marginBottom: '0.5rem',
                      color: 'var(--text-primary)',
                    }}
                  >
                    Forward To <span style={{ color: 'var(--danger)' }}>*</span>
                  </label>
                  <select
                    value={forwardToUserId || ''}
                    onChange={(e) => {
                      setForwardToUserId(e.target.value);
                      setValidationError(null);
                    }}
                    disabled={isSubmitting}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #cbd5e1',
                      borderRadius: '0.375rem',
                      fontSize: '0.875rem',
                      fontFamily: 'inherit',
                      background: 'var(--bg-primary)',
                    }}
                  >
                    <option value="">Select a user...</option>
                    {availableUsers.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.fullName} ({user.email})
                      </option>
                    ))}
                  </select>
                  <div style={{ marginTop: '1rem' }}>
                    <label
                      style={{
                        display: 'block',
                        fontSize: '1rem',
                        fontWeight: 600,
                        marginBottom: '0.5rem',
                        color: 'var(--text-primary)',
                      }}
                    >
                      Forward Reason <span style={{ color: 'var(--danger)' }}>*</span>
                    </label>
                    <textarea
                      value={forwardReason}
                      onChange={(e) => {
                        setForwardReason(e.target.value);
                        setValidationError(null);
                      }}
                      placeholder="Explain why you are forwarding this approval (minimum 10 characters)"
                      rows={3}
                      disabled={isSubmitting}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid #cbd5e1',
                        borderRadius: '0.375rem',
                        fontSize: '0.875rem',
                        fontFamily: 'inherit',
                        resize: 'vertical',
                      }}
                    />
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        fontSize: '0.75rem',
                        marginTop: '0.25rem',
                        color: forwardReason.trim().length < 10 ? '#dc2626' : '#64748b',
                      }}
                    >
                      <span>
                        {forwardReason.trim().length < 10
                          ? 'Minimum 10 characters required'
                          : 'Valid'}
                      </span>
                      <span>{forwardReason.trim().length} / 10</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Phase 2: Add Approvers - User Multi-Selection */}
              {selectedAction === 'add-approvers' && (
                <div style={{ marginBottom: '1.5rem' }}>
                  <label
                    style={{
                      display: 'block',
                      fontSize: '1rem',
                      fontWeight: 600,
                      marginBottom: '0.5rem',
                      color: 'var(--text-primary)',
                    }}
                  >
                    Select Approvers to Add <span style={{ color: 'var(--danger)' }}>*</span>
                  </label>
                  <div
                    style={{
                      border: '1px solid #cbd5e1',
                      borderRadius: '0.375rem',
                      maxHeight: '200px',
                      overflowY: 'auto',
                      background: 'var(--bg-primary)',
                    }}
                  >
                    {availableUsers.map((user) => (
                      <label
                        key={user.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: '0.75rem',
                          cursor: isSubmitting ? 'not-allowed' : 'pointer',
                          borderBottom: '1px solid #e5e7eb',
                          background: selectedApproverIds.includes(user.id) ? '#f0f9ff' : 'transparent',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={selectedApproverIds.includes(user.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedApproverIds([...selectedApproverIds, user.id]);
                            } else {
                              setSelectedApproverIds(selectedApproverIds.filter((id) => id !== user.id));
                            }
                            setValidationError(null);
                          }}
                          disabled={isSubmitting}
                          style={{ marginRight: '0.75rem' }}
                        />
                        <div>
                          <div style={{ fontSize: '0.875rem', fontWeight: 500 }}>{user.fullName}</div>
                          <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{user.email}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>
                    {selectedApproverIds.length} approver(s) selected
                  </div>

                  {/* Approval Mode Selection */}
                  <div style={{ marginTop: '1rem' }}>
                    <label
                      style={{
                        display: 'block',
                        fontSize: '1rem',
                        fontWeight: 600,
                        marginBottom: '0.5rem',
                        color: 'var(--text-primary)',
                      }}
                    >
                      Approval Mode <span style={{ color: 'var(--danger)' }}>*</span>
                    </label>
                    <div style={{ display: 'flex', gap: '1rem' }}>
                      <label
                        style={{
                          flex: 1,
                          padding: '0.75rem',
                          border: `2px solid ${approvalMode === 'sequential' ? '#06b6d4' : '#e2e8f0'}`,
                          background: approvalMode === 'sequential' ? '#f0fdfa' : 'white',
                          borderRadius: '0.375rem',
                          cursor: isSubmitting ? 'not-allowed' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <input
                          type="radio"
                          value="sequential"
                          checked={approvalMode === 'sequential'}
                          onChange={(e) => setApprovalMode(e.target.value as 'sequential' | 'parallel')}
                          disabled={isSubmitting}
                          style={{ marginRight: '0.5rem' }}
                        />
                        <div>
                          <div style={{ fontSize: '0.875rem', fontWeight: 600 }}>Sequential</div>
                          <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                            Approve one after another
                          </div>
                        </div>
                      </label>
                      <label
                        style={{
                          flex: 1,
                          padding: '0.75rem',
                          border: `2px solid ${approvalMode === 'parallel' ? '#06b6d4' : '#e2e8f0'}`,
                          background: approvalMode === 'parallel' ? '#f0fdfa' : 'white',
                          borderRadius: '0.375rem',
                          cursor: isSubmitting ? 'not-allowed' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <input
                          type="radio"
                          value="parallel"
                          checked={approvalMode === 'parallel'}
                          onChange={(e) => setApprovalMode(e.target.value as 'sequential' | 'parallel')}
                          disabled={isSubmitting}
                          style={{ marginRight: '0.5rem' }}
                        />
                        <div>
                          <div style={{ fontSize: '0.875rem', fontWeight: 600 }}>Parallel</div>
                          <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                            Approve simultaneously
                          </div>
                        </div>
                      </label>
                    </div>
                  </div>

                  {/* Addition Reason */}
                  <div style={{ marginTop: '1rem' }}>
                    <label
                      style={{
                        display: 'block',
                        fontSize: '1rem',
                        fontWeight: 600,
                        marginBottom: '0.5rem',
                        color: 'var(--text-primary)',
                      }}
                    >
                      Reason for Adding Approvers <span style={{ color: 'var(--danger)' }}>*</span>
                    </label>
                    <textarea
                      value={additionReason}
                      onChange={(e) => {
                        setAdditionReason(e.target.value);
                        setValidationError(null);
                      }}
                      placeholder="Explain why additional approval is needed (minimum 10 characters)"
                      rows={3}
                      disabled={isSubmitting}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid #cbd5e1',
                        borderRadius: '0.375rem',
                        fontSize: '0.875rem',
                        fontFamily: 'inherit',
                        resize: 'vertical',
                      }}
                    />
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        fontSize: '0.75rem',
                        marginTop: '0.25rem',
                        color: additionReason.trim().length < 10 ? '#dc2626' : '#64748b',
                      }}
                    >
                      <span>
                        {additionReason.trim().length < 10
                          ? 'Minimum 10 characters required'
                          : 'Valid'}
                      </span>
                      <span>{additionReason.trim().length} / 10</span>
                    </div>
                  </div>
                </div>
              )}

              {/* File Attachments Upload */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label
                  style={{
                    display: 'block',
                    fontSize: '1rem',
                    fontWeight: 600,
                    marginBottom: '0.5rem',
                    color: 'var(--text-primary)',
                  }}
                >
                  Attach Supporting Documents (Optional)
                </label>

                {/* Drag & Drop Zone */}
                <div
                  onDragOver={handleDragOver}
                  onDragEnter={handleDragEnter}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  style={{
                    border: isDragging ? '2px dashed #3b82f6' : '2px dashed #cbd5e1',
                    borderRadius: '0.5rem',
                    padding: '0.75rem 1rem',
                    textAlign: 'center',
                    background: isDragging ? '#eff6ff' : '#f8fafc',
                    transition: 'all 0.2s',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.75rem',
                  }}
                  onClick={() => document.getElementById('file-upload-input')?.click()}
                >
                  <IconSystem.upload size={24} color={isDragging ? '#3b82f6' : '#94a3b8'} />
                  <div style={{ textAlign: 'left' }}>
                    <p
                      style={{
                        margin: 0,
                        fontSize: '0.875rem',
                        color: '#475569',
                        fontWeight: 500,
                      }}
                    >
                      {isDragging ? 'Drop files here' : 'Drag & drop files here, or click to browse'}
                    </p>
                    <p style={{ margin: 0, fontSize: '0.75rem', color: '#94a3b8' }}>
                      PDF, Images, Documents (Max {formatFileSize(fileSizeLimits.maxIndividualSize)} per file, {formatFileSize(fileSizeLimits.maxTotalSize)} total)
                    </p>
                  </div>
                  <input
                    id="file-upload-input"
                    type="file"
                    multiple
                    onChange={handleFileSelect}
                    disabled={isSubmitting}
                    style={{ display: 'none' }}
                    accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
                  />
                </div>

                {/* Selected Files List */}
                {selectedFiles.length > 0 && (
                  <div style={{ marginTop: '1rem' }}>
                    <div
                      style={{
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        color: 'var(--text-secondary)',
                        marginBottom: '0.5rem',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <span>{selectedFiles.length} file(s) selected</span>
                      <span style={{ color: '#15803d' }}>
                        Total: {getFormattedTotalSize(selectedFiles.map((f) => f.file))} / {formatFileSize(fileSizeLimits.maxTotalSize)}
                      </span>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                      {selectedFiles.map((attachment, index) => {
                        const progress = uploadProgress[attachment.file.name] || 0;
                        const hasPreview = filePreviews[attachment.file.name];

                        return (
                          <div
                            key={`${attachment.file.name}-${index}`}
                            style={{
                              padding: '1rem',
                              background: 'var(--bg-secondary)',
                              border: '1px solid #e2e8f0',
                              borderRadius: '0.5rem',
                            }}
                          >
                            <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '0.75rem' }}>
                              {/* File Preview or Icon */}
                              <div
                                style={{
                                  width: '48px',
                                  height: '48px',
                                  borderRadius: '0.375rem',
                                  overflow: 'hidden',
                                  flexShrink: 0,
                                  background: `${getFileIconColor(attachment.file.type)}15`,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                {hasPreview ? (
                                  <img
                                    src={filePreviews[attachment.file.name]}
                                    alt={attachment.file.name}
                                    style={{
                                      width: '100%',
                                      height: '100%',
                                      objectFit: 'cover',
                                    }}
                                  />
                                ) : (
                                  <IconSystem.file size={24} color={getFileIconColor(attachment.file.type)} />
                                )}
                              </div>

                              {/* File Name and Size */}
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div
                                  style={{
                                    fontSize: '0.875rem',
                                    fontWeight: 600,
                                    color: 'var(--text-primary)',
                                    marginBottom: '0.25rem',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {attachment.file.name}
                                </div>
                                <div
                                  style={{
                                    fontSize: '0.75rem',
                                    color: 'var(--text-secondary)',
                                  }}
                                >
                                  {formatFileSize(attachment.file.size)}
                                </div>

                                {/* Upload Progress Bar */}
                                {progress < 100 && (
                                  <div style={{ marginTop: '0.5rem' }}>
                                    <div
                                      style={{
                                        width: '100%',
                                        height: '4px',
                                        background: '#e2e8f0',
                                        borderRadius: '2px',
                                        overflow: 'hidden',
                                      }}
                                    >
                                      <div
                                        style={{
                                          width: `${progress}%`,
                                          height: '100%',
                                          background: 'var(--primary)',
                                          transition: 'width 0.1s',
                                        }}
                                      />
                                    </div>
                                    <div
                                      style={{
                                        fontSize: '0.625rem',
                                        color: 'var(--text-secondary)',
                                        marginTop: '0.25rem',
                                      }}
                                    >
                                      Uploading... {progress}%
                                    </div>
                                  </div>
                                )}

                                {progress === 100 && (
                                  <div
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '0.25rem',
                                      fontSize: '0.75rem',
                                      color: '#10b981',
                                      fontWeight: 500,
                                      marginTop: '0.25rem',
                                    }}
                                  >
                                    <IconSystem.check size={14} />
                                    Ready
                                  </div>
                                )}
                              </div>

                              {/* Preview & Remove Buttons */}
                              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                {/* Preview Button (for images and PDFs) */}
                                {progress === 100 &&
                                  (attachment.file.type.startsWith('image/') ||
                                    attachment.file.type.includes('pdf')) && (
                                    <button
                                      type="button"
                                      onClick={() => {
                                        // For images, use the file preview URL
                                        if (attachment.file.type.startsWith('image/') && hasPreview) {
                                          setPreviewDocument({
                                            url: filePreviews[attachment.file.name],
                                            fileName: attachment.file.name,
                                            fileType: attachment.file.type,
                                          });
                                        } else {
                                          // For PDFs, create an object URL
                                          const objectUrl = URL.createObjectURL(attachment.file);
                                          setPreviewDocument({
                                            url: objectUrl,
                                            fileName: attachment.file.name,
                                            fileType: attachment.file.type,
                                          });
                                        }
                                      }}
                                      disabled={isSubmitting}
                                      style={{
                                        padding: '0.5rem',
                                        background: 'transparent',
                                        border: 'none',
                                        cursor: isSubmitting ? 'not-allowed' : 'pointer',
                                        color: '#10b981',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        borderRadius: '0.25rem',
                                        transition: 'background 0.2s',
                                        height: 'fit-content',
                                      }}
                                      onMouseEnter={(e) => {
                                        if (!isSubmitting) e.currentTarget.style.background = '#d1fae5';
                                      }}
                                      onMouseLeave={(e) => {
                                        e.currentTarget.style.background = 'transparent';
                                      }}
                                      title="Preview file"
                                    >
                                      <IconSystem.eye size={18} />
                                    </button>
                                  )}

                                {/* Remove Button */}
                                <button
                                  type="button"
                                  onClick={() => handleRemoveFile(index)}
                                  disabled={isSubmitting}
                                  style={{
                                    padding: '0.5rem',
                                    background: 'transparent',
                                    border: 'none',
                                    cursor: isSubmitting ? 'not-allowed' : 'pointer',
                                    color: 'var(--danger)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    borderRadius: '0.25rem',
                                    transition: 'background 0.2s',
                                    height: 'fit-content',
                                  }}
                                  onMouseEnter={(e) => {
                                    if (!isSubmitting) e.currentTarget.style.background = '#fee2e2';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.background = 'transparent';
                                  }}
                                  title="Remove file"
                                >
                                  <IconSystem.trash size={18} />
                                </button>
                              </div>
                            </div>

                            {/* Title Input */}
                            {progress === 100 && (
                              <div style={{ marginBottom: '0.75rem' }}>
                                <label
                                  style={{
                                    display: 'block',
                                    fontSize: '0.75rem',
                                    fontWeight: 600,
                                    color: 'var(--text-secondary)',
                                    marginBottom: '0.375rem',
                                  }}
                                >
                                  Document Name <span style={{ color: 'var(--danger)' }}>*</span>
                                </label>
                                <input
                                  type="text"
                                  value={attachment.title}
                                  onChange={(e) => handleUpdateAttachment(index, 'title', e.target.value)}
                                  placeholder="Enter document name"
                                  disabled={isSubmitting}
                                  style={{
                                    width: '100%',
                                    padding: '0.5rem',
                                    border: '1px solid #cbd5e1',
                                    borderRadius: '0.375rem',
                                    fontSize: '0.875rem',
                                    fontFamily: 'inherit',
                                  }}
                                />
                              </div>
                            )}

                            {/* Description Input */}
                            {progress === 100 && (
                              <div>
                                <label
                                  style={{
                                    display: 'block',
                                    fontSize: '0.75rem',
                                    fontWeight: 600,
                                    color: 'var(--text-secondary)',
                                    marginBottom: '0.375rem',
                                  }}
                                >
                                  Description (Optional)
                                </label>
                                <textarea
                                  value={attachment.description || ''}
                                  onChange={(e) => handleUpdateAttachment(index, 'description', e.target.value)}
                                  placeholder="Enter optional description"
                                  rows={2}
                                  disabled={isSubmitting}
                                  style={{
                                    width: '100%',
                                    padding: '0.5rem',
                                    border: '1px solid #cbd5e1',
                                    borderRadius: '0.375rem',
                                    fontSize: '0.875rem',
                                    fontFamily: 'inherit',
                                    resize: 'vertical',
                                  }}
                                />
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

            </>
          )}
        </div>

        {/* Footer — sticky; error banner sits directly above the action buttons
            so the user never has to scroll to see why submit failed. */}
        {currentUserPosition.canTakeAction && (
          <div
            style={{
              padding: '1rem 1.5rem',
              borderTop: '2px solid #e5e7eb',
              display: 'flex',
              flexDirection: 'column',
              gap: '0.75rem',
              background: 'var(--bg-secondary)',
              borderBottomLeftRadius: '0.5rem',
              borderBottomRightRadius: '0.5rem',
            }}
          >
            {validationError && (
              <div
                role="alert"
                style={{
                  padding: '0.625rem 0.875rem',
                  background: '#fef2f2',
                  border: '1px solid #fecaca',
                  borderRadius: '0.375rem',
                  color: '#991b1b',
                  fontSize: '0.875rem',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '0.5rem',
                }}
              >
                <span style={{ flex: 1 }}>{validationError}</span>
                <button
                  type="button"
                  onClick={() => setValidationError(null)}
                  aria-label="Dismiss error"
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#991b1b',
                    cursor: 'pointer',
                    padding: 0,
                    fontSize: '1rem',
                    lineHeight: 1,
                    flexShrink: 0,
                  }}
                >
                  ×
                </button>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
              <button
                onClick={handleClose}
                disabled={isSubmitting}
                style={{
                  padding: '0.625rem 1.25rem',
                  background: 'var(--bg-primary)',
                  border: '1px solid #cbd5e1',
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  color: '#475569',
                  opacity: isSubmitting ? 0.5 : 1,
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={!isFormValid || isSubmitting}
                style={{
                  padding: '0.625rem 1.25rem',
                  background: isFormValid && !isSubmitting ? '#3b82f6' : '#cbd5e1',
                  border: 'none',
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  cursor: isFormValid && !isSubmitting ? 'pointer' : 'not-allowed',
                  color: 'var(--color-text-inverse, #ffffff)',
                }}
              >
                {isSubmitting ? 'Submitting...' : `Submit ${selectedActionDetails?.label || 'Action'}`}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Confirmation Dialog Overlay */}
      {showConfirmation && selectedAction && selectedActionDetails && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            borderRadius: '0.5rem',
            padding: '1rem',
          }}
        >
          {/* Confirmation Card */}
          <div
            style={{
              background: 'var(--bg-primary)',
              borderRadius: '0.5rem',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
              maxWidth: '500px',
              width: '100%',
              maxHeight: '80vh',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* Header - Fixed */}
            <div
              style={{
                padding: '0.875rem 1.25rem',
                borderBottom: '1px solid #e5e7eb',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
              }}
            >
              <div
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  background: selectedActionDetails.color,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <IconSystem.alert size={18} color="white" />
              </div>
              <div>
                <h3
                  style={{
                    fontSize: '1rem',
                    fontWeight: 600,
                    color: 'var(--text-primary)',
                    margin: 0,
                  }}
                >
                  Confirm Action
                </h3>
                <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', margin: 0 }}>
                  Review details before submitting
                </p>
              </div>
            </div>

            {/* Body - Scrollable */}
            <div style={{ padding: '1.5rem', flex: 1, overflowY: 'auto', minHeight: 0 }}>
              {/* Action Type */}
              <div style={{ marginBottom: '1.25rem' }}>
                <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>
                  ACTION
                </div>
                <div
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: '0.5rem 0.75rem',
                    background: `${selectedActionDetails.color}15`,
                    border: `1px solid ${selectedActionDetails.color}40`,
                    borderRadius: '0.375rem',
                  }}
                >
                  <span style={{ fontSize: '0.875rem', fontWeight: 600, color: selectedActionDetails.color }}>
                    {selectedActionDetails.label}
                  </span>
                </div>
              </div>

              {/* Request Number */}
              <div style={{ marginBottom: '1.25rem' }}>
                <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>
                  REQUEST
                </div>
                <div style={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-primary)' }}>{ticket.ticketNo}</div>
              </div>

              {/* Comments */}
              <div style={{ marginBottom: '1.25rem' }}>
                <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>
                  COMMENTS
                </div>
                <div
                  style={{
                    padding: '0.75rem',
                    background: 'var(--bg-secondary)',
                    border: '1px solid #e2e8f0',
                    borderRadius: '0.375rem',
                    fontSize: '0.875rem',
                    color: '#475569',
                    maxHeight: '120px',
                    overflowY: 'auto',
                    fontStyle: comments.trim() ? 'normal' : 'italic',
                  }}
                >
                  {comments.trim() || 'No comments provided'}
                </div>
              </div>

              {/* Forward Details */}
              {selectedAction === 'forward' && forwardToUserId && (
                <div style={{ marginBottom: '1.25rem' }}>
                  <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>
                    FORWARDING TO
                  </div>
                  <div style={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-primary)', marginBottom: '0.5rem' }}>
                    {availableUsers.find((u) => u.id === forwardToUserId)?.fullName || 'Unknown User'}
                  </div>
                  {forwardReason.trim() && (
                    <>
                      <div
                        style={{
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          color: 'var(--text-secondary)',
                          marginTop: '0.75rem',
                          marginBottom: '0.5rem',
                        }}
                      >
                        REASON
                      </div>
                      <div
                        style={{
                          padding: '0.75rem',
                          background: 'var(--bg-secondary)',
                          border: '1px solid #e2e8f0',
                          borderRadius: '0.375rem',
                          fontSize: '0.875rem',
                          color: '#475569',
                        }}
                      >
                        {forwardReason}
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Return To Details */}
              {selectedAction === 'request-changes' && returnToUserId && (
                <div style={{ marginBottom: '1.25rem' }}>
                  <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>
                    RETURNING TO
                  </div>
                  {(() => {
                    const recipient = eligibleReturnRecipients.find(r => r.userId === returnToUserId);
                    return (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          padding: '0.5rem',
                          background: 'var(--bg-secondary)',
                          border: '1px solid #e2e8f0',
                          borderRadius: '0.375rem',
                        }}
                      >
                        <IconSystem.user size={16} color="#64748b" />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-primary)' }}>
                            {recipient?.userName || 'Unknown User'}
                          </div>
                          <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                            {recipient?.recipientType === 'Requestor'
                              ? 'Original Requestor'
                              : `${recipient?.levelName || `Level ${recipient?.levelNumber}`} - ${recipient?.actionTaken}`}
                          </div>
                        </div>
                        {recipient?.isRecommended && (
                          <span style={{ fontSize: '0.75rem', color: '#f59e0b', fontWeight: 500 }}>★ Recommended</span>
                        )}
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* Add Approvers Details */}
              {selectedAction === 'add-approvers' && selectedApproverIds.length > 0 && (
                <div style={{ marginBottom: '1.25rem' }}>
                  <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>
                    ADDING APPROVERS ({selectedApproverIds.length})
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '0.75rem' }}>
                    {selectedApproverIds.map((id) => {
                      const approver = availableUsers.find((a) => a.id === id);
                      return (
                        <div
                          key={id}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            padding: '0.5rem',
                            background: 'var(--bg-secondary)',
                            border: '1px solid #e2e8f0',
                            borderRadius: '0.375rem',
                          }}
                        >
                          <IconSystem.user size={16} color="#64748b" />
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-primary)' }}>
                              {approver?.fullName || 'Unknown'}
                            </div>
                            <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{approver?.role || 'N/A'}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div
                    style={{
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      color: 'var(--text-secondary)',
                      marginTop: '0.75rem',
                      marginBottom: '0.5rem',
                    }}
                  >
                    MODE: {approvalMode === 'sequential' ? 'Sequential' : 'Parallel'}
                  </div>
                  {additionReason.trim() && (
                    <>
                      <div
                        style={{
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          color: 'var(--text-secondary)',
                          marginTop: '0.75rem',
                          marginBottom: '0.5rem',
                        }}
                      >
                        REASON
                      </div>
                      <div
                        style={{
                          padding: '0.75rem',
                          background: 'var(--bg-secondary)',
                          border: '1px solid #e2e8f0',
                          borderRadius: '0.375rem',
                          fontSize: '0.875rem',
                          color: '#475569',
                        }}
                      >
                        {additionReason}
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* File Attachments */}
              {selectedFiles.length > 0 && (
                <div style={{ marginBottom: '1.25rem' }}>
                  <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>
                    ATTACHMENTS ({selectedFiles.length})
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '0.5rem',
                    }}
                  >
                    {selectedFiles.map((attachment, idx) => (
                      <div
                        key={idx}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          padding: '0.5rem 0.75rem',
                          background: 'var(--bg-secondary)',
                          border: '1px solid #e2e8f0',
                          borderRadius: '0.375rem',
                        }}
                      >
                        <IconSystem.file size={16} color="#64748b" />
                        <div style={{ flex: 1, fontSize: '0.875rem', color: 'var(--text-primary)' }}>
                          {attachment.title || attachment.file.name}
                        </div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                          {formatFileSize(attachment.file.size)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Warning */}
              <div
                style={{
                  padding: '0.75rem',
                  background: '#fef3c7',
                  border: '1px solid #fde68a',
                  borderRadius: '0.375rem',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '0.5rem',
                }}
              >
                <IconSystem.alert size={16} color="#92400e" style={{ flexShrink: 0, marginTop: '0.125rem' }} />
                <div style={{ fontSize: '0.75rem', color: '#92400e' }}>
                  This action cannot be undone. Please verify all details before confirming.
                </div>
              </div>
            </div>

            {/* Footer - Fixed */}
            <div
              style={{
                padding: '1rem 1.5rem',
                borderTop: '2px solid #e5e7eb',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '0.75rem',
                background: 'var(--bg-secondary)',
                flexShrink: 0,
              }}
            >
              <button
                onClick={() => setShowConfirmation(false)}
                disabled={isSubmitting}
                style={{
                  padding: '0.625rem 1.25rem',
                  background: 'var(--bg-primary)',
                  border: '1px solid #cbd5e1',
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  color: '#475569',
                  opacity: isSubmitting ? 0.5 : 1,
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmAndSubmit}
                disabled={isSubmitting}
                style={{
                  padding: '0.625rem 1.25rem',
                  background: !isSubmitting ? selectedActionDetails.color : '#cbd5e1',
                  border: 'none',
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  cursor: !isSubmitting ? 'pointer' : 'not-allowed',
                  color: 'var(--color-text-inverse, #ffffff)',
                }}
              >
                {isSubmitting ? 'Submitting...' : `Confirm ${selectedActionDetails.label}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Document Preview Modal */}
      {previewDocument && (
        <div
          onClick={() => {
            // Cleanup blob URL before closing
            if (previewDocument?.url) {
              window.URL.revokeObjectURL(previewDocument.url);
            }
            setPreviewDocument(null);
          }}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.9)',
            display: 'flex',
            flexDirection: 'column',
            zIndex: 11000,
            padding: '2rem',
          }}
        >
          {/* Preview Header */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '1rem',
              color: 'white',
            }}
          >
            <div>
              <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600 }}>Document Preview</h3>
              <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.875rem', opacity: 0.8 }}>
                {previewDocument.fileName}
              </p>
            </div>
            <button
              onClick={() => {
                // Cleanup blob URL before closing
                if (previewDocument?.url) {
                  window.URL.revokeObjectURL(previewDocument.url);
                }
                setPreviewDocument(null);
              }}
              style={{
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                color: 'white',
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'background 0.2s',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)')}
              title="Close preview"
            >
              <IconSystem.x size={24} />
            </button>
          </div>

          {/* Preview Content */}
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              flex: 1,
              background: 'white',
              borderRadius: '0.5rem',
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {previewDocument.fileType.startsWith('image/') ? (
              <img
                src={previewDocument.url}
                alt={previewDocument.fileName}
                style={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain',
                }}
              />
            ) : previewDocument.fileType.includes('pdf') ? (
              <iframe
                src={previewDocument.url}
                title={previewDocument.fileName}
                style={{
                  width: '100%',
                  height: '100%',
                  border: 'none',
                }}
              />
            ) : (
              <div style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>
                <IconSystem.file size={48} style={{ marginBottom: '1rem' }} />
                <p>Preview not available for this file type</p>
              </div>
            )}
          </div>

          {/* Preview Footer */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '1rem',
              marginTop: '1rem',
            }}
          >
            <button
              onClick={(e) => {
                e.stopPropagation();
                window.open(previewDocument.url, '_blank');
              }}
              style={{
                padding: '0.75rem 1.5rem',
                background: 'var(--primary)',
                color: 'white',
                border: 'none',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                fontWeight: 500,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                transition: 'opacity 0.2s',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.9')}
              onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
            >
              <IconSystem.download size={16} />
              Download
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TakeActionModal;
